import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { ChatBubbleBottomCenterTextIcon, SparklesIcon } from '@heroicons/react/24/outline';

// Chat conversation flow
const chatFlow = [
  { 
    id: 1, 
    type: 'customer', 
    message: 'Hi! I need help with my order', 
    delay: 1000 
  },
  { 
    id: 2, 
    type: 'typing', 
    delay: 2000, 
    duration: 1500 
  },
  { 
    id: 3, 
    type: 'business', 
    message: 'Hello! I\'d be happy to help you with your order. Can you please provide your order number?', 
    delay: 500 
  },
  { 
    id: 4, 
    type: 'customer', 
    message: '#ORD-12345', 
    delay: 2000 
  },
  { 
    id: 5, 
    type: 'typing', 
    delay: 1500, 
    duration: 2000 
  },
  { 
    id: 6, 
    type: 'business', 
    message: 'Perfect! I can see your order is being prepared and will be shipped today. You\'ll receive tracking information within 2 hours.', 
    delay: 500 
  },
  { 
    id: 7, 
    type: 'customer', 
    message: 'Thank you so much! 😊', 
    delay: 1500 
  },
  { 
    id: 8, 
    type: 'business', 
    message: 'You\'re welcome! Is there anything else I can help you with today?', 
    delay: 2000 
  }
];

const TypingIndicator = forwardRef((props, ref) => (
  <motion.div
    ref={ref}
    className="flex items-center space-x-1 bg-gray-200 rounded-lg p-3 mr-8"
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.8 }}
    transition={{ duration: 0.3 }}
  >
    <div className="flex space-x-1">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="w-2 h-2 bg-gray-500 rounded-full"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.5, 1, 0.5]
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            delay: i * 0.2
          }}
        />
      ))}
    </div>
    <span className="text-xs text-gray-500 ml-2">Snwolley is typing...</span>
  </motion.div>
));
TypingIndicator.displayName = 'TypingIndicator';

const ChatMessage = forwardRef(({ message, type, isLast }, ref) => {
  const isCustomer = type === 'customer';
  
  return (
    <motion.div
      ref={ref}
      className={`${isCustomer ? 'ml-8' : 'mr-8'}`}
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        duration: 0.4, 
        ease: "easeOut",
        type: "spring",
        stiffness: 200,
        damping: 20
      }}
    >
      <div
        className={`${
          isCustomer
            ? 'bg-gray-100 text-gray-700'
            : 'bg-gradient-to-r from-whatsapp-500 to-green-600 text-white'
        } rounded-lg p-3 relative`}
      >
        <p className="text-sm">{message}</p>
        
        {/* Message tail */}
        <div
          className={`absolute top-3 w-0 h-0 ${
            isCustomer
              ? 'left-[-6px] border-r-[6px] border-r-gray-100 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent'
              : 'right-[-6px] border-l-[6px] border-l-whatsapp-500 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent'
          }`}
        />
        
        {/* Read indicators for business messages */}
        {!isCustomer && (
          <motion.div
            className="flex justify-end mt-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex space-x-1">
              <div className="w-3 h-3 text-blue-200">
                <svg viewBox="0 0 16 15" className="w-full h-full fill-current">
                  <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l3.61 3.463c.143.14.361.125.484-.033L10.91 3.78a.366.366 0 0 0-.063-.512z"/>
                </svg>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
});
ChatMessage.displayName = 'ChatMessage';

export function WhatsAppChatAnimation() {
  const ref = useRef(null);
  const isInView = useInView(ref);
  const [showTyping, setShowTyping] = useState(false);
  const [completedMessages, setCompletedMessages] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [cycleCount, setCycleCount] = useState(0);

  useEffect(() => {
    let timeouts = [];

    if (isInView) {
      const runChatFlow = () => {
        setCompletedMessages([]);
        setIsComplete(false);
        setShowTyping(false);

        let cumulativeDelay = 500; // Initial delay

        chatFlow.forEach((step, index) => {
          cumulativeDelay += step.delay;

          if (step.type === 'typing') {
            // Show typing indicator
            timeouts.push(setTimeout(() => {
              setShowTyping(true);
            }, cumulativeDelay));

            // Hide typing indicator and add delay for typing duration
            cumulativeDelay += step.duration;
            timeouts.push(setTimeout(() => {
              setShowTyping(false);
            }, cumulativeDelay));
          } else {
            // Show message
            timeouts.push(setTimeout(() => {
              setCompletedMessages(prev => [...prev, step]);
            }, cumulativeDelay));
          }
        });

        // Mark as complete and schedule restart
        timeouts.push(setTimeout(() => {
          setIsComplete(true);
          setCycleCount(prev => prev + 1);
        }, cumulativeDelay + 1000));

        // Restart the animation
        timeouts.push(setTimeout(() => {
          runChatFlow();
        }, cumulativeDelay + 4000));
      };

      runChatFlow();
    } else {
      // Reset when out of view
      setCompletedMessages([]);
      setIsComplete(false);
      setShowTyping(false);
      setCycleCount(0);
    }

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [isInView]);

  return (
    <div
      ref={ref}
      className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-auto overflow-visible"
      style={{ margin: '3rem auto' }} // Add margin to prevent floating elements from being cut off
    >
      {/* Header */}
      <motion.div
        className="flex items-center space-x-3 mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="h-12 w-12 bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-full flex items-center justify-center relative">
          <ChatBubbleBottomCenterTextIcon className="h-6 w-6 text-white" />
          
          {/* Online indicator */}
          <motion.div
            className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">WhatsApp Business</h3>
          <motion.div
            className="flex items-center space-x-1"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <p className="text-sm text-green-600 font-medium">Online</p>
          </motion.div>
        </div>
      </motion.div>

      {/* Chat Messages */}
      <div className="space-y-4 min-h-[300px] max-h-[300px] overflow-hidden">
        <AnimatePresence mode="popLayout">
          {completedMessages
            .filter(step => step.type !== 'typing')
            .map((step, index) => (
              <ChatMessage
                key={`${cycleCount}-${step.id}`}
                message={step.message}
                type={step.type}
                isLast={index === completedMessages.filter(s => s.type !== 'typing').length - 1}
              />
            ))}
          
          {/* Typing Indicator */}
          {showTyping && <TypingIndicator key={`typing-${cycleCount}`} />}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <motion.div
        className="mt-6 flex items-center justify-center space-x-2 text-xs text-gray-500"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <SparklesIcon className="h-4 w-4" />
        <span>Powered by Snwolley • Response time: 0.8s</span>
      </motion.div>

              {/* Floating Stats - positioned to be fully visible */}
        <AnimatePresence>
          {isComplete && (
            <>
              <motion.div
                className="absolute -top-2 -left-2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg z-10"
                initial={{ opacity: 0, scale: 0, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                style={{ transform: 'translate(-25%, -25%)' }}
              >
                99.9% Uptime
              </motion.div>
              
              <motion.div
                className="absolute -bottom-2 -right-2 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg z-10"
                initial={{ opacity: 0, scale: 0, y: -20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
                style={{ transform: 'translate(25%, 25%)' }}
              >
                5min Setup
              </motion.div>
            </>
          )}
        </AnimatePresence>

      {/* Subtle Background Animation */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-4 right-4 w-20 h-20 bg-whatsapp-100 rounded-full opacity-20"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-8 left-8 w-16 h-16 bg-green-100 rounded-full opacity-20"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>
    </div>
  );
}
